import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { setLinking } from '@/lib/linkingState';


/**
 * Enforces "one identity per email across providers".
 * On every sign-in, calls claim_identity(); if the email is already linked
 * to a different user, signs the new session out and shows a toast.
 * Also syncs OAuth avatar_url into user_preferences.user_settings.avatarUrl
 * unless the user has manually overridden it (avatarSource === 'manual').
 */
export function useIdentityGuard() {
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const run = async (userId: string, email: string | null, provider: string, avatarUrl: string | null, fullName: string | null) => {
      if (handledRef.current === userId) return;
      handledRef.current = userId;

      setLinking(true);
      try {
        // 1. Identity claim
        if (email) {
          const { data, error } = await supabase.rpc('claim_identity' as any, {
            p_email: email,
            p_provider: provider,
          });
          if (!error && data && (data as any).ok === false && (data as any).reason === 'email_claimed_by_other_provider') {
            const existing = (data as any).existing_provider || 'another method';
            toast.error(
              `This email is already registered via ${existing}. Please sign in with that method to keep one account.`,
              { duration: 8000 }
            );
            await supabase.auth.signOut();
            return;
          }
        }

        // 2. Avatar / displayName sync (OAuth only, manual override wins).
        //
        // Apple never returns an avatar and often omits the name after the
        // first sign-in. Never let an Apple sign-in clobber an existing
        // avatar, `avatarSource`, or displayName — only fill in blanks.
        const isApple = provider === 'apple';
        const shouldSync = !!avatarUrl || (isApple && !!fullName);
        if (shouldSync) {
          try {
            const { data: pref } = await supabase
              .from('user_preferences')
              .select('user_settings')
              .eq('user_id', userId)
              .maybeSingle();
            const settings = (pref?.user_settings as any) || {};
            if (settings.avatarSource !== 'manual') {
              const nextSettings: Record<string, unknown> = { ...settings };
              // Only touch avatar fields when the provider actually gave us one.
              if (avatarUrl) {
                nextSettings.avatarUrl = avatarUrl;
                nextSettings.avatarSource = provider;
              }
              // Fill in displayName if empty — never overwrite.
              if (fullName && !settings.displayName) {
                nextSettings.displayName = fullName;
              }
              await supabase
                .from('user_preferences')
                .upsert(
                  { user_id: userId, user_settings: nextSettings as any },
                  { onConflict: 'user_id' }
                );
            }
          } catch (e) {
            console.warn('[useIdentityGuard] avatar sync failed', e);
          }
        }
      } finally {
        setLinking(false);
      }
    };


    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const u = session.user;
        const meta = (u.user_metadata || {}) as Record<string, any>;
        const provider = (u.app_metadata as any)?.provider || 'email';
        const avatarUrl = meta.avatar_url || meta.picture || null;
        const fullName = meta.full_name || meta.name || null;
        // Defer to next tick to avoid blocking the auth callback
        setTimeout(() => void run(u.id, u.email ?? null, provider, avatarUrl, fullName), 0);
      }
      if (event === 'SIGNED_OUT') handledRef.current = null;
    });
    return () => subscription.unsubscribe();
  }, []);
}
